import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { catalogSummary, internalGames, gameCategories, gameRegistry, type GameCategoryId } from '../../games/registry';
import { trendingGames } from './home-data';
import { GameEntryLink } from '../../games/shared/GameEntryLink';
import './classic-home.css';

type Filter = 'all' | GameCategoryId;

function prefersReducedMotion() {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function ClassicHomePage({ fallbackNotice }: { fallbackNotice?: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const routeNotice = (location.state as { fallbackNotice?: string } | null)?.fallbackNotice;
  const visibleFallbackNotice = fallbackNotice ?? routeNotice;
  const [featuredIndex, setFeaturedIndex] = useState(0);
  const [filter, setFilter] = useState<Filter>('all');
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || prefersReducedMotion()) return undefined;
    const timer = window.setInterval(() => {
      setFeaturedIndex((current) => (current + 1) % trendingGames.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [paused]);

  const visibleGames = useMemo(
    () => filter === 'all' ? gameRegistry : gameRegistry.filter((game) => game.category === filter),
    [filter],
  );

  const moveFeatured = (step: number) => {
    setFeaturedIndex((current) => (current + step + trendingGames.length) % trendingGames.length);
  };

  const chooseFilter = (nextFilter: Filter) => {
    setFilter(nextFilter);
    document.getElementById('classic-catalog')?.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  };

  const openRandomGame = () => {
    const game = internalGames[Math.floor(Math.random() * internalGames.length)];
    navigate(game.path);
  };

  return (
    <main className="classic-home">
      {visibleFallbackNotice && <div className="classic-home__fallback" role="status"><span>{visibleFallbackNotice}</span><Link to="/" state={{ retry3d: true }}>重试 3D</Link></div>}
      <div className="classic-home__page">
        <header className="classic-topbar">
          <Link className="classic-brand" to="/classic" aria-label="经典 2D 首页">
            <strong>gameplanet 小游戏收藏</strong><span>打开就玩</span>
          </Link>
          <nav className="classic-nav" aria-label="游戏分类">
            <button className={filter === 'all' ? 'active' : ''} onClick={() => chooseFilter('all')}>首页</button>
            {gameCategories.map((category) => (
              <button className={filter === category.id ? 'active' : ''} key={category.id} onClick={() => chooseFilter(category.id)}>
                {category.label}
              </button>
            ))}
          </nav>
          <div className="classic-top-actions">
            <Link to="/">进入 3D 星球</Link>
            <button type="button" onClick={openRandomGame}>随机开一局</button>
          </div>
        </header>

        <section className="classic-hero" aria-labelledby="featured-title">
          <div className="classic-feature" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocusCapture={() => setPaused(true)} onBlurCapture={() => setPaused(false)}>
            <div className="classic-feature__head"><h1 id="featured-title">Trending · 主推榜</h1><span>编辑推荐 · 不代表热度</span></div>
            <div className="classic-flip-stage">
              {trendingGames.map((game, index) => (
                <article
                  className={`classic-banner${index === featuredIndex ? ' active' : ''}`}
                  key={game.slug}
                  aria-hidden={index !== featuredIndex}
                  style={{ '--classic-accent': game.accent } as React.CSSProperties}
                >
                  <div className="classic-banner__art"><small>#{index + 1} · 编辑推荐 · {gameCategories.find((category) => category.id === game.category)?.label}</small><b>{game.symbol}</b></div>
                  <div className="classic-banner__info">
                    <div><em>Trending #{index + 1}</em><h2>{game.title}</h2><p>{game.subtitle}。这是编辑推荐的跨分类入口，不代表实时热度。</p></div>
                    <GameEntryLink game={game} tabIndex={index === featuredIndex ? 0 : -1}>立即打开 ↗</GameEntryLink>
                  </div>
                </article>
              ))}
              <div className="classic-carousel-controls" aria-label="推荐游戏轮播">
                <button type="button" aria-label="上一个推荐" onClick={() => moveFeatured(-1)}>‹</button>
                <div>
                  {trendingGames.map((game, index) => (
                    <button
                      className={index === featuredIndex ? 'active' : ''}
                      type="button"
                      key={game.slug}
                      aria-label={`查看推荐：${game.title}`}
                      aria-current={index === featuredIndex ? 'true' : undefined}
                      onClick={() => setFeaturedIndex(index)}
                    />
                  ))}
                </div>
                <button type="button" aria-label="下一个推荐" onClick={() => moveFeatured(1)}>›</button>
              </div>
            </div>
          </div>

          <aside className="classic-quick" aria-labelledby="quick-title">
            <p id="quick-title">Trending · 主推榜</p>
            <div>
              {trendingGames.map((game, index) => (
                <GameEntryLink game={game} key={game.slug} style={{ '--classic-accent': game.accent } as React.CSSProperties}>
                  <span>{String(index + 1).padStart(2, '0')}</span><span><strong>{game.title}</strong><small>{game.subtitle}</small></span><b aria-hidden="true">↗</b>
                </GameEntryLink>
              ))}
            </div>
          </aside>
        </section>

        <section className="classic-catalog-head" id="classic-catalog">
          <div><h2>全部游戏</h2><p>{filter === 'all' ? `${catalogSummary}，打开就能玩。` : `正在查看：${gameCategories.find((category) => category.id === filter)?.label}`}</p></div>
          <div className="classic-filters" aria-label="筛选游戏">
            <button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>全部</button>
            {gameCategories.map((category) => <button className={filter === category.id ? 'active' : ''} key={category.id} onClick={() => setFilter(category.id)}>{category.label}</button>)}
          </div>
        </section>

        <section className="classic-catalog" aria-label="游戏目录">
          {visibleGames.map((game, index) => (
            <GameEntryLink game={game} className="classic-game" key={game.slug} style={{ '--classic-accent': game.accent, '--classic-order': index } as React.CSSProperties}>
              <span className="classic-game__art"><b>{game.symbol}</b><span>{gameCategories.find((category) => category.id === game.category)?.label}</span></span>
              <h3>{game.title}</h3><small>{game.subtitle}。</small>
            </GameEntryLink>
          ))}
        </section>

        <footer className="classic-footer"><span>{catalogSummary} · {gameCategories.length} 类</span><span>纯前端 · 无需登录</span></footer>
      </div>
    </main>
  );
}
